import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { Heart, ArrowLeft, Music2, Video, Plus, Lock, ShoppingCart, Loader2, Share2, Facebook, Twitter, Linkedin, MessageCircle, Link2, Check } from "lucide-react";
import { mediaApi } from "../api";
import { usePlayer } from "../contexts/PlayerContext";
import { useMediaAuth } from "../contexts/MediaAuthContext";
import MediaRow from "../components/MediaRow";
import QualitySelector, { getSavedQuality, resolveQualityUrl, saveQuality } from "../components/QualitySelector";
import { pickCover } from "../components/MediaCard";
import useMediaSession from "../../hooks/useMediaSession";

// Remove referências a "vitamina(s) para a mente" do texto apresentado no
// detalhe do conteúdo (pedido do cliente), mantendo o restante texto.
const VITAMINA_RE = /vitaminas?\s+para\s+a\s+mente/gi;
// Remove tokens de data/ano (ex.: "Nov2014", "Nov 2014", "2014") da linha de
// metadados da descrição — pedido do cliente ("retira tb o ano").
const DATE_RE = /\b(?:jan|fev|mar|abr|mai|jun|jul|ago|set|out|nov|dez)[a-zç]*\.?\s*(?:de\s*)?(?:19|20)?\d{2,4}\b|\b(?:19|20)\d{2}\b/gi;
const stripVitamina = (s) =>
  String(s || "")
    .replace(VITAMINA_RE, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();

export default function MediaWatch() {
  const { id } = useParams();
  const nav = useNavigate();
  const player = usePlayer();
  const { user } = useMediaAuth();
  const [data, setData] = useState(null);
  const [fav, setFav] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [plOpen, setPlOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const [playError, setPlayError] = useState("");
  const [useFallback, setUseFallback] = useState(false);
  // Quando o <video> falha em Vimeo, tentamos o iframe do Vimeo como
  // último recurso (alguns browsers/codecs bloqueiam o external mp4).
  const [useVimeoIframe, setUseVimeoIframe] = useState(false);
  const [quality, setQuality] = useState(getSavedQuality());
  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState("");
  const savedRef = useRef(0);
  const resumeToRef = useRef(0);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const onQualityChange = (q) => {
    // Preserve playback position and playing state across src swap
    const vid = document.querySelector("video");
    if (vid) {
      resumeToRef.current = vid.currentTime || 0;
    }
    saveQuality(q);
    setQuality(q);
  };

  useEffect(() => {
    setData(null); setErr(""); setPlayError(""); setUseFallback(false); setUseVimeoIframe(false);
    (async () => {
      try {
        const d = await mediaApi.contentItem(id);
        setData(d);
        // Quando o conteúdo Vimeo não tem signed mp4 (apenas URL público),
        // arranca já em modo iframe para evitar o flash de "Formato não
        // suportado" e a sequência de erros do `<video>`.
        if (d?.item?.IsVimeo && d?.item?.PreferIframe) {
          setUseVimeoIframe(true);
        }
        // Chamadas pessoais (favoritos/progresso/played) só fazem sentido com
        // sessão — visitantes anónimos veem o conteúdo bloqueado com CTA de
        // login, sem disparar 401.
        if (user) {
          const favs = await mediaApi.favorites();
          setFav(favs.some((f) => f.ID_ConteudoMediaCenter === Number(id)));
          const prog = await mediaApi.getProgress(Number(id));
          if (prog && prog.position > 5 && prog.duration > 0 && prog.position < prog.duration - 15) {
            resumeToRef.current = prog.position;
          } else {
            resumeToRef.current = 0;
          }
          await mediaApi.played(Number(id));
        } else {
          resumeToRef.current = 0;
        }
      } catch (e) {
        setErr(e.message || "Erro");
      }
    })();
  }, [id, user]);

  const loadPlaylists = async () => {
    try { setPlaylists(await mediaApi.playlists()); }
    catch (e) { console.error("Load playlists failed", e); }
  };

  const toggleFav = async () => {
    try {
      if (fav) { await mediaApi.favRemove(Number(id)); setFav(false); }
      else { await mediaApi.favAdd(Number(id)); setFav(true); }
    } catch (e) { console.error("Toggle favorite failed", e); }
  };

  const addToPlaylist = async (pid) => {
    try {
      await mediaApi.playlistAddItem(pid, Number(id));
      setPlOpen(false);
    } catch (e) { console.error("Add to playlist failed", e); }
  };

  // ─── Partilha social (referral) ───────────────────────────────────
  // Junta ?refer=<ID_Cliente> ao URL da página atual do conteúdo; se o
  // cliente não estiver ligado usa 1.
  const referId = user?.ID_Cliente || 1;
  const shareUrl = `${window.location.origin}${window.location.pathname}?refer=${referId}`;
  const shareText = data?.item?.Descricao || "IHTP Media Center";
  const shareTargets = [
    { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}` },
    { key: "facebook", label: "Facebook", Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { key: "twitter", label: "X (Twitter)", Icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}` },
    { key: "linkedin", label: "LinkedIn", Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
  ];

  const openShare = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=640");
    setShareOpen(false);
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBuy = async () => {
    setBuying(true);
    setBuyError("");
    try {
      const res = await mediaApi.cartAdd(Number(id), 1);
      // Redirecciona para o checkout do MY. O conteúdo desbloqueia
      // automaticamente após pagamento (cron `legacy_audit_mediacenter_offers`
      // liga compra paga → conteúdo).
      window.location.href = res?.redirect || "/my/checkout";
    } catch (e) {
      setBuyError(e.message || "Não foi possível adicionar ao carrinho.");
      setBuying(false);
    }
  };

  // ─── Media Session API (lock-screen / Control Center / car displays) ───
  // O hook tem de ser sempre chamado (Rules of Hooks) — chamamos antes
  // dos early returns. Quando ainda não temos `data` ou o conteúdo está
  // bloqueado, passamos metadata vazia → o hook é um no-op.
  const item = data?.item;
  // Locked se `CanPlay` for explicitamente 0/false OU `Owned=false` (catálogo/teaser).
  // Teasers têm sempre `Owned=true` (ver backend `teaser-audios`).
  const isLocked = item
    ? !(item.CanPlay === true || item.CanPlay === 1
        || item.Owned === true
        || item.IsTeaser === true)
      && (item.CanPlay === false || item.CanPlay === 0 || item.Owned === false)
    : false;
  const relatedRaw = data
    ? [...data.children, ...data.siblings.filter((s) => s.ID_ConteudoMediaCenter !== item.ID_ConteudoMediaCenter)]
    : [];
  const related = Array.from(new Map(relatedRaw.map((r) => [r.ID_ConteudoMediaCenter, r])).values());

  // Próxima/anterior faixa do MESMO formato (áudio↔áudio, vídeo↔vídeo)
  // para os botões prev/next funcionarem como esperado no lock-screen.
  const sameTypePeers = item
    ? related.filter((r) => Boolean(r.IsAudio) === Boolean(item.IsAudio))
    : [];
  const curIdx = item ? sameTypePeers.findIndex((r) => r.ID_ConteudoMediaCenter === item.ID_ConteudoMediaCenter) : -1;
  const nextItem = curIdx >= 0 && curIdx < sameTypePeers.length - 1
    ? sameTypePeers[curIdx + 1]
    : (sameTypePeers[0] && sameTypePeers[0].ID_ConteudoMediaCenter !== item?.ID_ConteudoMediaCenter ? sameTypePeers[0] : null);
  const prevItem = curIdx > 0 ? sameTypePeers[curIdx - 1] : null;

  const sessionMeta = item && !isLocked
    ? {
        title: item.Descricao || "",
        artist: "IHTP Media",
        album: "IHTP Media Center",
        artwork: (() => {
          const c = pickCover(item.ID_ConteudoMediaCenter);
          return [
            { src: c, sizes: "512x512", type: "image/jpeg" },
            { src: c, sizes: "256x256", type: "image/jpeg" },
          ];
        })(),
        onPrev: prevItem ? () => nav(`/media/watch/${prevItem.ID_ConteudoMediaCenter}`) : undefined,
        onNext: nextItem ? () => nav(`/media/watch/${nextItem.ID_ConteudoMediaCenter}`) : undefined,
      }
    : {};
  useMediaSession(item?.IsAudio ? audioRef : videoRef, sessionMeta);

  if (err) return <div className="px-6 py-40 text-center text-slate-400">{err}</div>;
  if (!data) return <div className="px-6 py-40 text-center"><div className="live-dot mx-auto" /></div>;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="px-4 md:px-8 lg:px-12 py-8 max-w-[1600px] mx-auto"
      data-testid="media-watch-page"
    >
      <button
        onClick={() => nav(-1)}
        className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#D1CFC9] transition"
        data-testid="media-watch-back"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      {/* Player ou Painel de Compra (conteúdo bloqueado) */}
      <div className={`relative mb-8 rounded-2xl overflow-hidden bg-black ${isLocked ? "" : item.IsAudio ? "aspect-[4/3] sm:aspect-[16/5]" : "aspect-video"}`}>
        {item.IsAudio && (
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1600&q=85"
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F2013]/30 via-[#0F1829]/80 to-[#0F1829]" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="w-24 h-24 rounded-full bg-[#D1CFC9]/20 border-2 border-[#D1CFC9]/40 grid place-items-center">
                <Music2 size={40} className="text-[#D1CFC9]" />
              </div>
            </div>
          </div>
        )}
        {isLocked ? (
          <div className="relative z-10 grid place-items-center bg-[#0F1829]/85 backdrop-blur px-4 py-10 sm:p-8 min-h-[260px] sm:min-h-[360px]" data-testid="media-watch-locked">
            <div className="max-w-md text-center space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/90 text-[10px] uppercase tracking-widest text-[#0F1829] font-bold">
                <Lock size={11} /> Conteúdo bloqueado
              </div>
              {item.PrecoPVP != null && (
                <div className="space-y-1" data-testid="media-watch-price">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">PVP</p>
                  <p
                    className="text-3xl sm:text-4xl font-bold text-amber-300 tabular-nums whitespace-nowrap"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {`${Number(item.PrecoPVP).toFixed(2).replace(".", ",")}\u00A0€`}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    IVA incluído ({Math.round(item.IvaPct ?? 23)}%)
                  </p>
                </div>
              )}
              {!user ? (
                <>
                  <p className="text-sm text-slate-300" data-testid="media-watch-login-required">
                    Faça login para aceder a este {item.IsAudio ? "áudio" : "vídeo"}.
                  </p>
                  <button
                    onClick={() => nav(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                    data-testid="media-watch-login-cta"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-[#0F1829] text-sm font-bold uppercase tracking-wider hover:bg-amber-300 transition"
                  >
                    <Lock size={16} /> Faça login para aceder
                  </button>
                  <p className="text-[10px] text-slate-500 tracking-wider uppercase">
                    Após iniciar sessão volta automaticamente a este conteúdo
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-300">
                    Adquira este {item.IsAudio ? "áudio" : "vídeo"} para começar a ouvir/ver. A compra fica
                    associada à sua conta e o desbloqueio é automático após o pagamento.
                  </p>
                  <button
                    onClick={handleBuy}
                    disabled={buying}
                    data-testid="media-watch-buy"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-[#0F1829] text-sm font-bold uppercase tracking-wider hover:bg-amber-300 disabled:opacity-60 transition"
                  >
                    {buying ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />}
                    {buying ? "A adicionar…" : "Comprar — adicionar ao carrinho"}
                  </button>
                  {buyError && (
                    <p className="text-xs text-red-400" data-testid="media-watch-buy-error">{buyError}</p>
                  )}
                  <p className="text-[10px] text-slate-500 tracking-wider uppercase">
                    Será redireccionado para o checkout do MyIHTP
                  </p>
                </>
              )}
            </div>
          </div>
        ) : item.MediaUrl ? (
          <div className="absolute inset-0 z-10">
            {item.IsAudio ? (
              <audio
                key={`${item.ID_ConteudoMediaCenter}`}
                ref={audioRef}
                controls
                preload="metadata"
                className="w-full h-full"
                style={{ background: "#000" }}
                src={item.MediaUrl}
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration || 0;
                  player.setDuration(d);
                  if (resumeToRef.current > 0) {
                    try { e.currentTarget.currentTime = resumeToRef.current; }
                    catch { /* video not yet seekable — resume on next loadedmetadata */ }
                    resumeToRef.current = 0;
                  }
                }}
                onTimeUpdate={(e) => {
                  const t = e.currentTarget.currentTime || 0;
                  const d = e.currentTarget.duration || 0;
                  const now = Date.now();
                  if (now - savedRef.current > 10000 && t > 1) {
                    savedRef.current = now;
                    mediaApi.saveProgress(Number(id), t, d, false).catch(() => {});
                  }
                }}
                onEnded={() => mediaApi.saveProgress(Number(id), 0, 0, true).catch(() => {})}
              />
            ) : useVimeoIframe && item.IsVimeo && item.VimeoEmbedUrl ? (
              // Fallback final para Vimeo: o <video> falhou (codec/CORS). Tentamos
              // o iframe oficial — pode mostrar "Sorry, privacy" se o domínio não
              // estiver whitelisted no Vimeo, mas é melhor que um erro silencioso.
              <iframe
                key={`vimeo-${item.ID_ConteudoMediaCenter}`}
                title={item.Descricao || "Vídeo"}
                src={item.VimeoEmbedUrl}
                className="w-full h-full"
                style={{ background: "#000", border: 0 }}
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                data-testid="media-watch-iframe"
              />
            ) : (
              <video
                key={`${item.ID_ConteudoMediaCenter}-${useFallback ? "fb" : quality}`}
                ref={videoRef}
                controls
                controlsList="nodownload"
                playsInline
                preload="metadata"
                className="w-full h-full"
                style={{ background: "#000" }}
                src={useFallback && item.FallbackUrl
                  ? item.FallbackUrl
                  : resolveQualityUrl(item.Qualities, quality, item.MediaUrl)}
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration || 0;
                  player.setDuration(d);
                  mediaApi.saveProgress(Number(id), 0, d, false).catch(() => {});
                  if (resumeToRef.current > 0) {
                    try { e.currentTarget.currentTime = resumeToRef.current; }
                    catch { /* video not yet seekable — resume on next loadedmetadata */ }
                    resumeToRef.current = 0;
                  }
                }}
                onTimeUpdate={(e) => {
                  const t = e.currentTarget.currentTime || 0;
                  const d = e.currentTarget.duration || 0;
                  const now = Date.now();
                  if (now - savedRef.current > 10000 && t > 1) {
                    savedRef.current = now;
                    mediaApi.saveProgress(Number(id), t, d, false).catch(() => {});
                  }
                }}
                onEnded={() => mediaApi.saveProgress(Number(id), 0, 0, true).catch(() => {})}
                onError={(e) => {
                  const mediaErr = e?.currentTarget?.error;
                  if (!mediaErr) return;
                  if (mediaErr.code === 20) return; // ABORTED
                  const codeMsg = {
                    1: "Reprodução cancelada",
                    2: "Erro de rede",
                    3: "Erro a descodificar",
                    4: "Formato não suportado",
                  }[mediaErr.code] || "Erro ao carregar conteúdo";
                  console.warn("Video error:", codeMsg, mediaErr, "src=", e.currentTarget.currentSrc);
                  // Para Vimeo, salta direto para o iframe (o FallbackUrl=iframe
                  // page e não funciona em <video>); para os restantes mantém o
                  // fluxo de qualidade/fallback original.
                  if (item.IsVimeo && item.VimeoEmbedUrl) {
                    setUseVimeoIframe(true);
                  } else if (!useFallback && item.FallbackUrl) {
                    setUseFallback(true);
                  } else if (quality !== "auto") {
                    // Try reverting to default quality before giving up
                    setQuality("auto");
                  } else {
                    setPlayError(codeMsg);
                  }
                }}
              />
            )}
            {item.IsVideo && !item.IsVimeo && !playError && (
              <QualitySelector
                qualities={item.Qualities || []}
                current={quality}
                onChange={onQualityChange}
              />
            )}
            {playError && (
              <div className="absolute inset-0 grid place-items-center bg-[#0F1829]/95 backdrop-blur z-20 p-6 text-center" data-testid="media-watch-error">
                <div className="max-w-md space-y-3">
                  <div className="text-[#D1CFC9] text-xs tracking-widest uppercase font-bold">Conteúdo indisponível</div>
                  <h3 className="font-serif text-2xl">{playError}</h3>
                  <p className="text-sm text-slate-400">
                    Este conteúdo pode estar restrito pelo fornecedor. Tenta abrir no fornecedor original.
                  </p>
                  {(item.FallbackUrl || item.MediaUrl) && (
                    <a
                      href={item.FallbackUrl || item.MediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="media-btn-ghost inline-flex items-center gap-2 mt-2"
                      data-testid="media-watch-external-link"
                    >
                      Abrir no fornecedor
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-slate-400">Conteúdo indisponível</div>
        )}
      </div>

      {/* Meta */}
      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start mb-10">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#D1CFC9] font-bold mb-2 flex items-center gap-2">
            {item.IsAudio ? <Music2 size={12} /> : <Video size={12} />}
            {item.IsAudio ? "Áudio" : "Vídeo"}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-4 leading-tight">{stripVitamina(item.Descricao)}</h1>
          {(item.Tag1 || item.Tag2 || item.Tag3) && (
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4" data-testid="media-watch-tags">
              {[item.Tag1, item.Tag2, item.Tag3].filter(Boolean).map((t, i) => (
                <span key={`watch-tag-${i}`}
                  className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-white/10 border border-white/10 text-[10px] sm:text-xs tracking-wide text-[#D1CFC9]">
                  {t}
                </span>
              ))}
            </div>
          )}
          {item.Resumo && (
            <div className="w-full mb-5 p-4 sm:p-5 rounded-xl bg-white/5 border border-white/10" data-testid="media-watch-resumo">
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#978D78] mb-2 font-bold">Sobre este conteúdo</p>
              <p className="text-slate-200 text-[15px] sm:text-sm leading-relaxed whitespace-pre-wrap">{stripVitamina(item.Resumo)}</p>
            </div>
          )}
          {item.ConteudoHtmlMobile && (() => {
            // Strip anchor/link tags and any media-file references (e.g. "objetivos-foco.mp3")
            // from the description. Requested: "não mostrar links/nome dos ficheiros".
            const FILE_EXT_RE = /[\w\-.]+\.(mp3|mp4|m4a|m4v|wav|ogg|webm|mov|avi|mkv|flv|wmv|aac|flac|aif+)\b/gi;
            const cleaned = String(item.ConteudoHtmlMobile || "")
              .replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, "")
              .replace(VITAMINA_RE, "")
              .replace(DATE_RE, "")
              .replace(FILE_EXT_RE, "")
              .replace(/&nbsp;/g, " ")
              .replace(/^[·•\-\s]+/g, "")
              .replace(/[·•\-\s]+$/g, "")
              .trim();
            if (!cleaned.replace(/<[^>]+>/g, "").trim()) return null;
            return (
              <p className="text-slate-300 text-[15px] sm:text-sm leading-relaxed w-full"
                 dangerouslySetInnerHTML={{
                   __html: DOMPurify.sanitize(cleaned, { USE_PROFILES: { html: true } })
                 }}
              />
            );
          })()}
        </div>
        <div className="flex gap-2">
          {user && (
          <button
            onClick={toggleFav}
            className={`media-btn-ghost inline-flex items-center gap-2 ${fav ? "!text-[#D1CFC9] !border-[#D1CFC9]/50" : ""}`}
            data-testid="media-watch-favorite"
          >
            <Heart size={16} fill={fav ? "currentColor" : "none"} />
            {fav ? "Favorito" : "Adicionar"}
          </button>
          )}
          {user && (
          <div className="relative">
            <button
              onClick={() => { loadPlaylists(); setPlOpen((v) => !v); }}
              className="media-btn-ghost inline-flex items-center gap-2"
              data-testid="media-watch-playlist-btn"
            >
              <Plus size={16} /> Playlist
            </button>
            {plOpen && (
              <div className="absolute right-0 mt-2 min-w-[240px] glass-panel rounded-xl p-2 z-30" data-testid="media-watch-playlist-menu">
                {playlists.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-slate-400">Ainda não tens playlists.</div>
                ) : (
                  playlists.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => addToPlaylist(p.id)}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-white/10"
                      data-testid={`media-watch-playlist-${p.id}`}
                    >
                      {p.name} <span className="text-xs text-slate-500">({p.count})</span>
                    </button>
                  ))
                )}
                <button
                  onClick={() => { setPlOpen(false); nav("/media/playlists"); }}
                  className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-white/10 text-[#D1CFC9] mt-1 border-t border-white/5"
                >
                  + Gerir playlists
                </button>
              </div>
            )}
          </div>
          )}
          <div className="relative">
            <button
              onClick={() => { setShareOpen((v) => !v); setPlOpen(false); }}
              className="media-btn-ghost inline-flex items-center gap-2"
              data-testid="media-watch-share-btn"
            >
              <Share2 size={16} /> Partilhar
            </button>
            {shareOpen && (
              <div className="absolute right-0 mt-2 min-w-[220px] glass-panel rounded-xl p-2 z-30" data-testid="media-watch-share-menu">
                {shareTargets.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => openShare(t.href)}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-white/10 inline-flex items-center gap-2.5"
                    data-testid={`media-watch-share-${t.key}`}
                  >
                    <t.Icon size={16} className="text-[#D1CFC9]" /> {t.label}
                  </button>
                ))}
                <button
                  onClick={copyShareLink}
                  className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-white/10 inline-flex items-center gap-2.5 text-[#D1CFC9] mt-1 border-t border-white/5"
                  data-testid="media-watch-share-copy"
                >
                  {copied ? <Check size={16} /> : <Link2 size={16} />}
                  {copied ? "Link copiado!" : "Copiar link"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <MediaRow rowKey="related" title="Relacionados" items={related} />
      )}
    </motion.div>
  );
}
